'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import type { ElasticBranch } from '@//app/api/search/branch/[id]';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { InlineButton } from '@//components/InlineButton';
import { Link } from '@//components/Link';
import { useLegacyTrackPhoneNumberCall } from '@//data/tracking/useLegacyTrackPhoneNumberCall';
import { useLegacyTrackPhoneNumberVisible } from '@//data/tracking/useLegacyTrackPhoneNumberVisible';
import { formatPhoneNumber, formatPhoneNumberPretty } from '@//utils/phonenumbers';
import styles from './SellerSection.module.scss';

interface SellerTelephoneProps {
  branch: ElasticBranch;
  vehicle: ElasticVehicle;
}

const SellerTelephone = ({ branch, vehicle }: SellerTelephoneProps) => {
  const tCommon = useTranslations('other.common.telephoneNumber');
  const [isVisible, setIsVisible] = useState(false);
  const { trackLegacyPhoneNumberCall } = useLegacyTrackPhoneNumberCall();
  const { trackLegacyPhoneNumberVisible } = useLegacyTrackPhoneNumberVisible();

  const onShowClick = useCallback(() => {
    trackLegacyPhoneNumberVisible(vehicle);
    setIsVisible(true);
  }, [trackLegacyPhoneNumberVisible, vehicle]);

  const onPhoneLinkClick = useCallback(() => {
    trackLegacyPhoneNumberCall(vehicle);
  }, [trackLegacyPhoneNumberCall, vehicle]);

  const formattedPhoneNumber = branch.telephoneNumber
    ? formatPhoneNumber(branch.telephoneNumber)
    : undefined;

  const formattedPhoneNumberPretty = branch.telephoneNumber
    ? formatPhoneNumberPretty(branch.telephoneNumber)
    : undefined;

  if (!branch.telephoneNumber) return null;

  return (
    <>
      <dt className={styles.asideListLabel}>
        <abbr title={tCommon('label')}>{tCommon('short')}</abbr>
      </dt>
      <dd className={styles.asideListValue}>
        {isVisible ? (
          <Link
            href={`tel:${formattedPhoneNumber}`}
            title={tCommon('linkAriaLabel', {
              name: branch.name,
              phoneNumber: formattedPhoneNumberPretty ?? '',
            })}
            onClick={onPhoneLinkClick}
          >
            {formattedPhoneNumberPretty}
          </Link>
        ) : (
          <InlineButton onClick={onShowClick} weight="strong">
            {tCommon('showTelephoneNumber')}
          </InlineButton>
        )}
      </dd>
    </>
  );
};

export default SellerTelephone;
